'use client';

import { useState, useEffect } from 'react';
import { 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  User,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';

interface AdminBookingsProps {
  sessionToken: string;
  onDataChange: () => void;
}

const AdminBookings = ({ sessionToken, onDataChange }: AdminBookingsProps): JSX.Element => {
  const [bookings, setBookings] = useState<any[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editForm, setEditForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    check_in: '',
    check_out: '',
    total_price: '',
    status: 'pending',
    special_requests: ''
  });

  useEffect(() => {
    loadBookings();
  }, [sessionToken]);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchTerm, statusFilter]);

  const loadBookings = async () => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = bookings;

    if (searchTerm) {
      filtered = filtered.filter((booking: any) =>
        booking.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.guest_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.guest_phone.includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((booking: any) => booking.status === statusFilter);
    }

    setFilteredBookings(filtered);
  };

  const handleEdit = (booking: any) => {
    setSelectedBooking(booking);
    setEditForm({
      guest_name: booking.guest_name,
      guest_email: booking.guest_email,
      guest_phone: booking.guest_phone,
      check_in: booking.check_in,
      check_out: booking.check_out,
      total_price: booking.total_price,
      status: booking.status,
      special_requests: booking.special_requests || ''
    });
    setIsEditModalOpen(true);
  };

  const handleView = (booking: any) => {
    setSelectedBooking(booking);
    setIsViewModalOpen(true);
  };

  const handleCreate = () => {
    setEditForm({
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      check_in: '',
      check_out: '',
      total_price: '350',
      status: 'pending',
      special_requests: ''
    });
    setIsCreateModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedBooking) {
        // Update existing booking
        const { data, error } = await supabase
          .from('bookings')
          .update(editForm)
          .eq('id', selectedBooking.id)
          .select()
          .single();

        if (error) throw error;

        // Send email notification
        await sendBookingEmail(data, 'update');
      } else {
        // Create new booking
        const { data, error } = await supabase
          .from('bookings')
          .insert(editForm)
          .select()
          .single();

        if (error) throw error;

        // Send email notification
        await sendBookingEmail(data, 'confirmation');
      }
      
      await loadBookings();
      onDataChange();
      setIsEditModalOpen(false);
      setIsCreateModalOpen(false);
      setSelectedBooking(null);
    } catch (error) {
      console.error('Error saving booking:', error);
    }
  };

  const sendBookingEmail = async (booking: any, type: 'confirmation' | 'update' | 'cancellation') => {
    try {
      const emailData = {
        bookingId: booking.id,
        guestName: booking.guest_name,
        guestEmail: booking.guest_email,
        guestPhone: booking.guest_phone,
        checkIn: booking.check_in,
        checkOut: booking.check_out,
        totalPrice: booking.total_price.toString(),
        specialRequests: booking.special_requests,
        type
      };

      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-booking-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });
    } catch (error) {
      console.error('Error sending booking email:', error);
    }
  };

  const handleDelete = async (bookingId: string) => {
    console.log('Delete button clicked for booking ID:', bookingId);
    console.log('Current user session:', await supabase.auth.getSession());
    
    const confirmed = window.confirm('Are you sure you want to delete this booking? This action cannot be undone.');
    console.log('User confirmed deletion:', confirmed);
    
    if (!confirmed) {
      console.log('User cancelled deletion');
      return;
    }

    try {
      console.log('Starting deletion process for booking:', bookingId);
      
      // Get booking details before deletion for email
      const bookingToDelete = bookings.find((b: any) => b.id === bookingId);
      console.log('Found booking to delete:', bookingToDelete);
      
      // Test if we can access the booking first
      const { data: testData, error: testError } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();
      
      console.log('Test select result:', { testData, testError });
      
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', bookingId);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }
      
      console.log('Booking deleted successfully from database');

      // Send cancellation email
      if (bookingToDelete) {
        try {
          console.log('Sending cancellation email...');
          await sendBookingEmail(bookingToDelete, 'cancellation');
          console.log('Cancellation email sent successfully');
        } catch (emailError) {
          console.error('Failed to send cancellation email:', emailError);
          // Continue with deletion even if email fails
        }
      }
      
      console.log('Reloading bookings list...');
      await loadBookings();
      onDataChange();
      
      // Show success message
      console.log('Delete operation completed successfully');
      alert('Booking deleted successfully!');
    } catch (error) {
      console.error('Error deleting booking:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to delete booking: ${errorMessage}. Please try again.`);
    }
  };

  // Test function to check database connection
  const testDatabaseConnection = async () => {
    try {
      console.log('Testing database connection...');
      const { count, error } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true });
      
      console.log('Database test result:', { count, error });
      
      if (error) {
        alert(`Database connection error: ${error.message}`);
      } else {
        alert(`Database connection successful. Found ${count || 0} bookings.`);
      }
    } catch (error) {
      console.error('Database test error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(`Database test failed: ${errorMessage}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const Modal = ({ isOpen, onClose, title, children }: any) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#7C8F75]/10">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#333333]">{title}</h2>
              <Button onClick={onClose} variant="ghost" size="icon">
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Booking Management</h1>
          <p className="text-[#333333]/70">Manage all guest bookings and reservations</p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={testDatabaseConnection}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            Test DB
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] hover:from-[#7C8F75]/90 hover:to-[#A8B89A]/90 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Booking
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#7C8F75]/10 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Label htmlFor="search" className="text-[#333333] font-medium mb-2 block">Search Bookings</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#333333]/40" />
              <Input
                id="search"
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
          </div>
          <div className="md:w-48">
            <Label className="text-[#333333] font-medium mb-2 block">Status Filter</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="border-[#7C8F75]/30 focus:border-[#7C8F75]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-[#7C8F75]/10 overflow-hidden overflow-x-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7C8F75]"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 text-[#333333]/60">
            No bookings found
          </div>
        ) : (
          <div className="min-w-full overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-[#F9F6F2] border-b border-[#7C8F75]/10">
                <tr>
                  <th className="text-left p-3 sm:p-4 font-semibold text-[#333333] min-w-[200px]">Guest</th>
                  <th className="text-left p-3 sm:p-4 font-semibold text-[#333333] min-w-[180px]">Dates</th>
                  <th className="text-left p-3 sm:p-4 font-semibold text-[#333333] min-w-[120px]">Status</th>
                  <th className="text-left p-3 sm:p-4 font-semibold text-[#333333] min-w-[100px]">Total</th>
                  <th className="text-left p-3 sm:p-4 font-semibold text-[#333333] min-w-[150px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking: any) => (
                  <tr key={booking.id} className="border-b border-[#7C8F75]/10 hover:bg-[#F9F6F2]/50">
                    <td className="p-3 sm:p-4">
                      <div>
                        <div className="font-semibold text-[#333333] text-sm sm:text-base">{booking.guest_name}</div>
                        <div className="text-sm text-[#333333]/60">{booking.guest_email}</div>
                        <div className="text-sm text-[#333333]/60">{booking.guest_phone}</div>
                      </div>
                    </td>
                    <td className="p-3 sm:p-4">
                      <div className="text-[#333333] text-sm sm:text-base">
                        {new Date(booking.check_in).toLocaleDateString()} - {new Date(booking.check_out).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-[#333333]/60">
                        {Math.ceil((new Date(booking.check_out).getTime() - new Date(booking.check_in).getTime()) / (1000 * 60 * 60 * 24))} nights
                      </div>
                    </td>
                    <td className="p-3 sm:p-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(booking.status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 sm:p-4">
                      <div className="font-semibold text-[#7C8F75] text-sm sm:text-base">${booking.total_price}</div>
                    </td>
                    <td className="p-3 sm:p-4">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <Button
                          onClick={() => handleView(booking)}
                          variant="ghost"
                          size="icon"
                          className="text-[#7C8F75] hover:bg-[#7C8F75]/10"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(booking)}
                          variant="ghost"
                          size="icon"
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(booking.id)}
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-50"
                          title="Delete booking"
                          type="button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Booking Details"
      >
        {selectedBooking && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-[#7C8F75]" />
                  <div>
                    <div className="font-semibold text-[#333333]">{selectedBooking.guest_name}</div>
                    <div className="text-sm text-[#333333]/60">Guest Name</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-[#7C8F75]" />
                  <div>
                    <div className="font-semibold text-[#333333]">{selectedBooking.guest_email}</div>
                    <div className="text-sm text-[#333333]/60">Email Address</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-[#7C8F75]" />
                  <div>
                    <div className="font-semibold text-[#333333]">{selectedBooking.guest_phone}</div>
                    <div className="text-sm text-[#333333]/60">Phone Number</div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-[#7C8F75]" />
                  <div>
                    <div className="font-semibold text-[#333333]">
                      {new Date(selectedBooking.check_in).toLocaleDateString()} - {new Date(selectedBooking.check_out).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-[#333333]/60">Check-in - Check-out</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <DollarSign className="h-5 w-5 text-[#7C8F75]" />
                  <div>
                    <div className="font-semibold text-[#333333]">${selectedBooking.total_price}</div>
                    <div className="text-sm text-[#333333]/60">Total Price</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusIcon(selectedBooking.status)}
                  <div>
                    <div className={`font-semibold ${getStatusColor(selectedBooking.status)} px-2 py-1 rounded-full text-xs`}>
                      {selectedBooking.status.charAt(0).toUpperCase() + selectedBooking.status.slice(1)}
                    </div>
                    <div className="text-sm text-[#333333]/60">Status</div>
                  </div>
                </div>
              </div>
            </div>
            {selectedBooking.special_requests && (
              <div>
                <h3 className="font-semibold text-[#333333] mb-2">Special Requests</h3>
                <p className="text-[#333333]/80 bg-[#F9F6F2] p-4 rounded-xl">{selectedBooking.special_requests}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Booking"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Guest Name</Label>
              <Input
                id="edit-name"
                value={editForm.guest_name}
                onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.guest_email}
                onChange={(e) => setEditForm({ ...editForm, guest_email: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={editForm.guest_phone}
                onChange={(e) => setEditForm({ ...editForm, guest_phone: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                <SelectTrigger className="border-[#7C8F75]/30 focus:border-[#7C8F75]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-checkin">Check-in Date</Label>
              <Input
                id="edit-checkin"
                type="date"
                value={editForm.check_in}
                onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-checkout">Check-out Date</Label>
              <Input
                id="edit-checkout"
                type="date"
                value={editForm.check_out}
                onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-price">Total Price</Label>
            <Input
              id="edit-price"
              type="number"
              value={editForm.total_price}
              onChange={(e) => setEditForm({ ...editForm, total_price: e.target.value })}
              className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-requests">Special Requests</Label>
            <Textarea
              id="edit-requests"
              value={editForm.special_requests}
              onChange={(e) => setEditForm({ ...editForm, special_requests: e.target.value })}
              className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              onClick={() => setIsEditModalOpen(false)}
              variant="outline"
              className="border-[#7C8F75] text-[#7C8F75]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] text-white"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Booking"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="create-name">Guest Name</Label>
              <Input
                id="create-name"
                value={editForm.guest_name}
                onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
                placeholder="Enter guest name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                value={editForm.guest_email}
                onChange={(e) => setEditForm({ ...editForm, guest_email: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
                placeholder="Enter email address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-phone">Phone</Label>
              <Input
                id="create-phone"
                value={editForm.guest_phone}
                onChange={(e) => setEditForm({ ...editForm, guest_phone: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-status">Status</Label>
              <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                <SelectTrigger className="border-[#7C8F75]/30 focus:border-[#7C8F75]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-checkin">Check-in Date</Label>
              <Input
                id="create-checkin"
                type="date"
                value={editForm.check_in}
                onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-checkout">Check-out Date</Label>
              <Input
                id="create-checkout"
                type="date"
                value={editForm.check_out}
                onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })}
                className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-price">Total Price</Label>
            <Input
              id="create-price"
              type="number"
              value={editForm.total_price}
              onChange={(e) => setEditForm({ ...editForm, total_price: e.target.value })}
              className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              placeholder="350"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-requests">Special Requests</Label>
            <Textarea
              id="create-requests"
              value={editForm.special_requests}
              onChange={(e) => setEditForm({ ...editForm, special_requests: e.target.value })}
              className="border-[#7C8F75]/30 focus:border-[#7C8F75]"
              rows={3}
              placeholder="Any special requests or notes..."
            />
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              onClick={() => setIsCreateModalOpen(false)}
              variant="outline"
              className="border-[#7C8F75] text-[#7C8F75]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] text-white"
            >
              Create Booking
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminBookings;